import { makeAutoObservable, computed } from 'mobx';
import { WordSayerInterface } from '@/models/word-sayer-interface';
import { ValueModel } from '@/lib/models/value-models';
import { ButtonAction } from '@/lib/models/actions';
import { RangeSelectable } from '@/lib/models/range-selectable';
import { LettersRow } from './letters-row';
import { WordsToFind } from '@/models/finders/words-to-find';
import { WordToFind } from '@/models/finders/word-to-find';
import { DifficultyType } from './types';
import { EmotionalWordSayer } from '@/models/audio/emotional-word-sayer';
import { HappyOrSad } from '@/models/audio/emotion-types';

export class WordsInRowFinder implements RangeSelectable {
  wordSayer: WordSayerInterface;
  emotionalWordSayer: EmotionalWordSayer<HappyOrSad>;
  difficulty: ValueModel<DifficultyType>;
  forwardsOnly: ValueModel<boolean>;
  auto: ValueModel<boolean>;
  lettersRow: LettersRow;
  wordsToFind: WordsToFind;
  taskStarted: boolean = false;
  newWordsCallback: () => string[];
  autoTimeoutId: number | null = null;

  constructor(wordSayer: WordSayerInterface, emotionalWordSayer: EmotionalWordSayer<HappyOrSad>, words: string[] = [], newWordsCallback: () => string[]) {
    this.wordSayer = wordSayer;
    this.emotionalWordSayer = emotionalWordSayer;
    this.newWordsCallback = newWordsCallback;

    this.difficulty = new ValueModel<DifficultyType>(
      'easy',
      'Difficulty',
      'Easy: random letters. Hard: includes letters from the word'
    );

    this.forwardsOnly = new ValueModel<boolean>(
      true,
      'Forwards only',
      'If checked, words only appear forwards. If unchecked, words can appear backwards too'
    );

    this.auto = new ValueModel<boolean>(
      false,
      'Auto',
      'If checked, automatically selects the next word after finding one correctly'
    );

    this.lettersRow = new LettersRow();
    this.wordsToFind = new WordsToFind(words);

    makeAutoObservable(this, {
      canChangeSettings: computed,
      newAction: computed,
      showNewButton: computed,
      canSelect: computed
    });
  }

  get canChangeSettings(): boolean {
    // Can change settings before task starts or after completion
    if (!this.taskStarted || this.wordsToFind.completed) {
      return true;
    }

    // During task: can't change settings if user is actively trying to find a word
    const activeWord = this.wordsToFind.activeWord;
    if (activeWord && activeWord.found !== true) {
      return false;
    }

    // During task: can change settings if no active word or active word was found
    return true;
  }

  get canSelect(): boolean {
    return !this.lettersRow.interactionsDisabled;
  }

  get newAction(): ButtonAction {
    const handler = this.showNewButton ? () => this.new() : null;
    return new ButtonAction(handler, { tooltip: 'Start with a new set of words' });
  }

  get showNewButton(): boolean {
    return this.wordsToFind.completed;
  }

  async selectWordToFind(wordToFind: WordToFind): Promise<void> {
    if (!wordToFind.canClick) return;

    // Clear any pending auto-selection since user is manually selecting
    this.clearAutoSelection();

    if (!this.taskStarted) {
      this.taskStarted = true;
    }

    const wasAlreadyActive = this.wordsToFind.activeWord === wordToFind;
    this.wordsToFind.setActiveWord(wordToFind);

    // Only populate the letters row if this is a new active word
    if (!wasAlreadyActive) {
      this.lettersRow.populate(
        wordToFind.word,
        this.difficulty.value,
        this.forwardsOnly.value
      );
    }

    await this.wordSayer.say(wordToFind.word);
  }

  startSelection(position: number): void {
    if (!this.wordsToFind.activeWord) return;

    // Clear wrong state when starting a new selection
    if (this.wordsToFind.activeWord.found === false) {
      this.wordsToFind.activeWord.found = null;
    }

    this.lettersRow.startSelection(position, this.forwardsOnly.value);
  }

  updateSelection(endPosition: number): void {
    this.lettersRow.updateSelection(endPosition);
  }

  async finishSelection(): Promise<void> {
    if (!this.lettersRow.selectionState || !this.wordsToFind.activeWord) {
      this.lettersRow.clearSelection();
      return;
    }

    const isCorrect = this.lettersRow.checkSelectedWord();

    if (isCorrect) {
      this.wordsToFind.markActiveWordCorrect();
      this.lettersRow.markSelectionCorrect();
      this.scheduleAutoSelection();
    } else {
      this.wordsToFind.markActiveWordWrong();
      this.lettersRow.markSelectionWrong();
      // Play a random sad word for incorrect selection
      await this.emotionalWordSayer.playRandomWord('sad');
    }
  }

  clearSelection(): void {
    this.lettersRow.clearSelection();
  }

  private scheduleAutoSelection(): void {
    // Clear any existing timeout
    this.clearAutoSelection();

    // Only schedule if auto is enabled and there are more words to find
    if (!this.auto.value || this.wordsToFind.completed) {
      return;
    }

    this.autoTimeoutId = window.setTimeout(() => {
      this.autoTimeoutId = null;
      this.performAutoSelection();
    }, 1000);
  }

  private clearAutoSelection(): void {
    if (this.autoTimeoutId !== null) {
      clearTimeout(this.autoTimeoutId);
      this.autoTimeoutId = null;
    }
  }

  private async performAutoSelection(): Promise<void> {
    const randomWord = this.wordsToFind.randomUnfoundWord;
    if (randomWord) {
      await this.selectWordToFind(randomWord);
    }
  }

  new(): void {
    const newWords = this.newWordsCallback();
    this.setWords(newWords);
  }

  setWords(words: string[]): void {
    this.wordsToFind = new WordsToFind(words);
    this.reset();
  }

  reset(): void {
    this.clearAutoSelection();
    this.taskStarted = false;
    this.wordsToFind.reset();
    this.lettersRow.clearSelection();
    this.lettersRow.letters = null;
    this.lettersRow.word = '';
    this.lettersRow.correctSelection = null;
    this.lettersRow.wrongSelection = null;
    this.lettersRow.correctSelectionStart = null;
    this.lettersRow.wrongSelectionStart = null;
  }

}
